import { useEffect, useState } from 'react'
import type { RunStep } from '../types'
import { ACTION_INFO, explainStep } from '../copy/explain'
import { useSharpened } from '../lib/progressive'
import { Elapsed } from './DecisionTrace'
import { IconClock, IconSpinner } from '../icons'

/**
 * 用户那张图，钉在页面上；跑着的时候进度就压在图上。
 *
 * 背景：2026-09-11 用户裁决——「图片要一直在界面上，不要消失」。在此之前，
 *   排队、诊断、处理这三屏各自只有一条步骤轨迹：用户点了「开始」之后，
 *   自己那张图就从页面上消失，换成一串滚动的日志，一分多钟里没有任何东西
 *   能证明服务端收到的是他刚选的那张。
 *
 *   图放回来之后，日志还留在图下面自成一段（大标题 + 副标题 + 整条时间线），
 *   同日又被否：「下面这坨不太好，这个 UI 能不能就在原图上啊」。确实——
 *   跑的时候整页只有一件事在发生，为它另起一个区块，页面就被切成
 *   「图」和「进度」两块互相争视线的东西。
 * 设计意图：进度压在图的下沿，一条玻璃条，两行：上行是「跑到哪了」（第几步、已耗时），
 *   下行是「在做什么」（转圈 + 该步的实测说明）。不写「正在诊断」那一行——
 *   标签已经说了是诊断的第几步，图标已经说了它在跑，那一行是三者里最泛的一个。
 *   完整的决策轨迹不在这儿——它在完成页的「它是怎么判的」里，跑的时候摊开七步，
 *   用户也无从判断，只会觉得吵。
 * 关键约束：
 *   1. 地址优先用浏览器手里那份 blob。服务端的回显要等作业目录落盘，
 *      而这一屏在排队时就要出现；等回显会先空一格再闪出来。
 *   2. 整幅是缩放显示的，这里**不做**任何标注或滤镜。加个扫描线之类的动效
 *      会盖在画面上，用户分不清那是我们画的还是他图里本来就有的。
 *      诊断标出的红色是另一回事（见 ProblemMap），那是有坐标依据的。
 *   3. 跑着的时候，图本身也在变：每跑完一步，管线都落了一张图，服务端把名字报出来
 *      （见 app/jobs/worker.py 的 emit_preview），这里就把它模糊着叠上来
 *      （2026-09-11 用户裁决：「过程图也可以实时更新到这里。可以模糊一下，
 *      然后模糊渐变过渡过来」）。留一点残余模糊不是装饰：过程图不是成品，
 *      有的只清了一半，有的是给模型看的参考图，清清楚楚摆出来会被当成交付物
 *      去挑毛病。角上那枚「过程图」也是为这个。
 *   4. 只给已耗时，不给进度条，也不给预估剩余（同 DecisionTrace 的 Elapsed）：
 *      耗时取决于外部出图接口，给一个会跳变或停在 90% 的进度条比不给更糟。
 *   5. 高度限死在视口的一半以内：竖图（手机截屏常见）按原比例能顶到三屏高，
 *      那样玻璃条会被挤到屏幕外面去。
 */
/**
 * 过程图一层层叠上来。
 *
 * 直接换 `<img>` 的 src 换不出渐变：浏览器会一直显示旧的那张，等新的解码完了
 * **一下**跳过去。所以新的一张先在后台解码，解码完再作为新的一层入场，
 * 从模糊渐显压住前一层——两层就够了，再往前的都被不透明的那层挡住。
 */
function useLiveFrames(src?: string | null): { src: string; on: boolean }[] {
  const [frames, setFrames] = useState<{ src: string; on: boolean }[]>([])
  useEffect(() => {
    if (!src) {
      setFrames([])
      return
    }
    let dead = false
    const enter = () => {
      if (dead) return
      setFrames(f => (f[f.length - 1]?.src === src ? f : [...f.slice(-1), { src, on: false }]))
      // 两帧之后再打开：同一帧里挂上去又改类名，浏览器不会当成过渡，直接就位。
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!dead) setFrames(f => f.map(x => (x.src === src ? { ...x, on: true } : x)))
      }))
    }
    const img = new Image()
    img.src = src
    img.decode().then(enter).catch(enter)
    return () => { dead = true }
  }, [src])
  return frames
}

export function YourImage({ src, soft, status, busy = false, steps, caption, live }: {
  src: string
  /**
   * 同尺寸的模糊占位图，只在图来自服务端时给（浏览器手里那份 blob 是本地的，不需要）。
   * 给了就先摆它，原图到了再换掉——见 lib/progressive 的 useSharpened。
   */
  soft?: string | null
  /** 角标上那行字，例如「诊断完成」。跑着的时候图上是玻璃条，不用它。 */
  status: string
  busy?: boolean
  /** 已经跑过的步骤。给了就把当前这一步压在图上，不给就只挂一枚角标。 */
  steps?: RunStep[]
  /** 图下面的一句说明。不给就不占位置。 */
  caption?: string
  /**
   * 处理途中最新的那张图。给了就叠在原图上，模糊着渐变过来。
   *
   * 底下那张原图不撤：它定着这个框的尺寸，而过程图是铺满这个框的（同一张图的
   * 各道工序，尺寸一致）。中途取不到图（作业跑完过程图就回收了）也只是叠不上来，
   * 框和进度条都还在。
   */
  live?: string | null
}) {
  const now = steps?.length ? steps[steps.length - 1] : null
  const frames = useLiveFrames(live)
  const shown = useSharpened(src, soft)

  return (
    <figure className="mine">
      <div className="mine-frame">
        <img src={shown.src} alt="你上传的图" />
        {frames.map(f => (
          <img key={f.src} className={f.on ? 'mine-live on' : 'mine-live'}
               src={f.src} alt="" aria-hidden />
        ))}
        {/* 只在有进度条那一版挂：没有进度条时左上角是状态角标，两枚会叠在一起。 */}
        {frames.length > 0 && now && <span className="mine-tag mini">过程图</span>}
        {now ? (
          <div className="mine-run">
            {/* 第几步与已耗时都是「跑到哪了」，同属一行；下面那行才是「在做什么」。
                这两样挤进标题行时，「正在诊断」被夹在中间，一眼看不出主次。 */}
            <div className="mine-run-meta">
              <span className="mine-run-tag">
                {ACTION_INFO[now.action]?.label ?? now.action}·第 {now.n} 步
              </span>
              <Elapsed className="mine-run-elapsed" />
            </div>
            {/* 不再写一行「正在诊断」：上面那枚标签已经说了是诊断的第几步，
                转圈的图标已经说了它在跑，这一句说的是此刻在做什么——三样都在说
                同一件事时，最泛的那一行该走。这一句由该步的实测数值生成
                （explainStep），不是写死的进度话术。 */}
            <p className="mine-run-say">
              <IconSpinner size={15} />
              <span>{explainStep(now, busy)}</span>
            </p>
          </div>
        ) : (
          <span className={busy ? 'mine-tag busy' : 'mine-tag'}>
            {busy && <IconClock size={13} />}
            {status}
          </span>
        )}
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  )
}
